import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SwapHoriz, Today, CalendarToday, DateRange } from '@mui/icons-material';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';
import TransactionModal from '../../components/admin/transactions/TransactionModal';
import TransactionTable from '../../components/admin/transactions/TransactionTable';
import TransactionFilters from '../../components/admin/transactions/TransactionFilters';
import TransactionTabs from '../../components/admin/transactions/TransactionTabs';
import TransactionStats from '../../components/admin/transactions/TransactionStats';
import { useAuth } from '../../contexts/AuthContext';
import { fetchTransactions, formatDateForAPI } from '../../services/transactionService';
import { fetchCaissierTerminal, fetchCompanyPos } from '../../services/posService';
import { fetchPaymentLinks } from '../../services/paymentLinkService';
import { fetchPaymentRequests } from '../../services/paymentRequestService';
import {
    buildTransactionCategoryIndex,
    partitionTransactionsByCategory,
} from '../../utils/transactionCategory';

const TimeRangeButton = ({ active, onClick, children, icon: Icon }) => (
    <button
        type="button"
        onClick={onClick}
        className={`
            flex items-center space-x-2 px-3 py-2.5 rounded-xl font-semibold transition-all duration-300 text-sm
            ${active
                ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25'
                : 'text-slate-600 hover:text-indigo-700 hover:bg-indigo-50/50 border border-slate-200 hover:border-indigo-200'
            }
        `}
    >
        <Icon className={active ? 'text-white text-lg' : 'text-indigo-600 text-lg'} />
        <span>{children}</span>
    </button>
);

const CurrencyButton = ({ active, onClick, children, isBusy }) => (
    <button
        type="button"
        onClick={onClick}
        className={`
            flex items-center space-x-2 px-3 py-2.5 rounded-xl font-semibold transition-all duration-300 text-sm
            ${active
                ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25'
                : 'text-slate-600 hover:text-indigo-700 hover:bg-indigo-50/50 border border-slate-200 hover:border-indigo-200'
            }
        `}
    >
        <span className="flex items-center space-x-2">
            <span>{children}</span>
            {isBusy && active && (
                <span className="w-3 h-3 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
            )}
        </span>
    </button>
);

const CashierTransactionsPage = () => {
    const { selectedOrganization, currentRole, user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [caissierTerminal, setCaissierTerminal] = useState(null);
    const [caissierTerminalLoading, setCaissierTerminalLoading] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [activeTab, setActiveTab] = useState('all');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionsData, setTransactionsData] = useState({
        all: [],
        paymentRequests: [],
        paymentLinks: [],
        withdrawals: [],
        deposits: []
    });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        operation: 'all',
        service: 'all',
        actor: 'all',
        ref: 'all'
    });
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currencyLoading, setCurrencyLoading] = useState(false);

    // Pour distinguer réellement : demande de paiement vs lien de paiement
    const [idsReady, setIdsReady] = useState(false);
    const [categoryIndex, setCategoryIndex] = useState(() =>
        buildTransactionCategoryIndex()
    );
    const hasLoadedOnceRef = useRef(false);

    const loadCaissierTerminal = useCallback(async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        try {
            setCaissierTerminalLoading(true);
            const terminal = await fetchCaissierTerminal(selectedOrganization.id, user.id);
            setCaissierTerminal(terminal || null);
        } catch (error) {
            console.error('Error loading caissier terminal:', error);
            setCaissierTerminal(null);
        } finally {
            setCaissierTerminalLoading(false);
        }
    }, [selectedOrganization?.id, user?.id]);

    useEffect(() => {
        const loadIds = async () => {
            if (!selectedOrganization?.id) {
                setIdsReady(false);
                setCategoryIndex(buildTransactionCategoryIndex());
                return;
            }
            try {
                const pos = await fetchCompanyPos(selectedOrganization.id);
                const posIds = (pos || []).map((p) => p.id).filter(Boolean);
                const emt = user?.id || 'all';

                const [linkRes, prRes] = await Promise.all([
                    fetchPaymentLinks({
                        pge: 1,
                        lmt: 500,
                        sts: 'all',
                        org: selectedOrganization.id,
                        emt,
                        cmr: 'all',
                        slg: '',
                    }),
                    fetchPaymentRequests({
                        pge: 1,
                        lmt: 500,
                        sts: 'all',
                        org: selectedOrganization.id,
                        emt,
                        cmr: 'all',
                        tag: 'all',
                        slg: '',
                    }),
                ]);

                const links = linkRes?.contents || [];
                const linkIds = links.map((l) => l.id).filter(Boolean);

                const requests = prRes?.contents || [];
                const paymentRequestIds = requests.map((r) => r.id).filter(Boolean);
                const terminalFromRequests = requests
                    .map((r) => r.terminal?.id || r.terminal_id)
                    .filter(Boolean);
                const terminalIds = [...new Set([...posIds, ...terminalFromRequests])];

                setCategoryIndex(
                    buildTransactionCategoryIndex({
                        terminalIds,
                        paymentLinkIds: linkIds,
                        paymentRequestIds,
                    })
                );
            } catch (e) {
                console.error('[CashierTransactionsPage] Error loading terminal/link ids:', e);
                setCategoryIndex(buildTransactionCategoryIndex());
            } finally {
                setIdsReady(true);
            }
        };
        loadIds();
    }, [selectedOrganization?.id, user?.id]);

    const loadTransactions = useCallback(async ({ silent = false } = {}) => {
        if (!selectedOrganization || !user?.id) {
            setLoading(false);
            setCurrencyLoading(false);
            return;
        }
        if (!idsReady) {
            setLoading(false);
            setCurrencyLoading(false);
            return;
        }

        try {
            const shouldSilent = silent || hasLoadedOnceRef.current;
            if (shouldSilent) setCurrencyLoading(true);
            else setLoading(true);

            const solutionId = localStorage.getItem('netikash_solution');
            const now = new Date();
            let startDate = new Date();
            if (timeRange === 'today') {
                startDate.setHours(0, 0, 0, 0);
            } else if (timeRange === 'week') {
                startDate.setDate(now.getDate() - 7);
            } else if (timeRange === 'month') {
                startDate.setMonth(now.getMonth() - 1);
            }

            const params = {
                own: selectedOrganization.id,
                sol: solutionId,
                srv: filters.service || 'all',
                act: user.id,
                ref: filters.ref || 'all',
                opr: filters.operation || 'all',
                slug: filters.search || '',
                crc: 'all', // liste toutes devises ; la bascule CDF/USD pilote les statistiques
                sts: filters.status || 'all',
                fdt: formatDateForAPI(startDate),
                tdt: formatDateForAPI(now),
                lmt: 20,
                pge: currentPage
            };

            const response = await fetchTransactions(params);
            const transactions = response.contents || [];
            setNextPage(response.nextPage || null);
            setPrevPage(response.previousPage || null);

            setTransactionsData(partitionTransactionsByCategory(transactions, categoryIndex));
        } catch (error) {
            console.error('Error loading transactions:', error);
            setTransactionsData({
                all: [],
                paymentRequests: [],
                paymentLinks: [],
                withdrawals: [],
                deposits: []
            });
        } finally {
            setLoading(false);
            setCurrencyLoading(false);
            hasLoadedOnceRef.current = true;
        }
    }, [selectedOrganization, user?.id, timeRange, currency, filters, currentPage, idsReady, categoryIndex]);

    useEffect(() => {
        if (selectedOrganization?.id && user?.id) loadCaissierTerminal();
    }, [selectedOrganization?.id, user?.id, loadCaissierTerminal]);

    useEffect(() => {
        if (!selectedOrganization || !user?.id) {
            setLoading(false);
            setCurrencyLoading(false);
            setTransactionsData({ all: [], paymentRequests: [], paymentLinks: [], withdrawals: [], deposits: [] });
            return;
        }
        loadTransactions({ silent: hasLoadedOnceRef.current });
    }, [loadTransactions, selectedOrganization, user?.id]);

    return (
        <div className="min-h-screen bg-white flex">
            <CashierSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="transactions"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden flex flex-col lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                <div className="bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 min-h-screen flex flex-col flex-1">
                    <CashierHeader
                        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        terminal={caissierTerminal}
                        loading={caissierTerminalLoading}
                    />

                    <div className="flex-1 p-4 lg:p-6 relative z-10 space-y-6 overflow-y-auto">
                        {/* Barre période + devise (style HD, cohérent admin) */}
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <TimeRangeButton
                                    active={timeRange === 'today'}
                                    onClick={() => { setTimeRange('today'); setCurrentPage(1); }}
                                    icon={Today}
                                >
                                    <span className="hidden sm:inline">Aujourd'hui</span>
                                    <span className="sm:hidden">Jour</span>
                                </TimeRangeButton>
                                <TimeRangeButton
                                    active={timeRange === 'week'}
                                    onClick={() => { setTimeRange('week'); setCurrentPage(1); }}
                                    icon={CalendarToday}
                                >
                                    <span className="hidden sm:inline">Semaine</span>
                                    <span className="sm:hidden">Sem</span>
                                </TimeRangeButton>
                                <TimeRangeButton
                                    active={timeRange === 'month'}
                                    onClick={() => { setTimeRange('month'); setCurrentPage(1); }}
                                    icon={DateRange}
                                >
                                    <span className="hidden sm:inline">Mois</span>
                                    <span className="sm:hidden">Mois</span>
                                </TimeRangeButton>
                            </div>
                            <div className="flex items-center gap-1">
                                <CurrencyButton
                                    active={currency === 'USD'}
                                    onClick={() => { setCurrency('USD'); setCurrentPage(1); }}
                                    isBusy={currencyLoading}
                                >
                                    USD
                                </CurrencyButton>
                                <CurrencyButton
                                    active={currency === 'CDF'}
                                    onClick={() => { setCurrency('CDF'); setCurrentPage(1); }}
                                    isBusy={currencyLoading}
                                >
                                    CDF
                                </CurrencyButton>
                            </div>
                        </div>

                        {/* En-tête page */}
                        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded-2xl p-6 text-indigo-900 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="p-2 bg-indigo-500/10 rounded-xl">
                                            <SwapHoriz className="text-xl text-indigo-600" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-indigo-900">
                                                Transactions
                                            </h1>
                                            <p className="text-indigo-700">
                                                Vos transactions sur la période sélectionnée
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <TransactionStats
                            currency={currency}
                            timeRange={timeRange}
                            transactions={transactionsData.all || []}
                            loading={loading}
                        />

                        <TransactionTabs
                            activeTab={activeTab}
                            onTabChange={(tab) => { setActiveTab(tab); setCurrentPage(1); }}
                            counts={{
                                all: transactionsData.all?.length || 0,
                                paymentRequests: transactionsData.paymentRequests?.length || 0,
                                paymentLinks: transactionsData.paymentLinks?.length || 0
                            }}
                        />

                        <TransactionFilters
                            activeTab={activeTab}
                            filters={filters}
                            onFiltersChange={(newFilters) => { setFilters(newFilters); setCurrentPage(1); }}
                            counts={(() => {
                                const list = transactionsData[activeTab] || [];
                                return {
                                    all: list.length,
                                    completed: list.filter(t => (t.status || '').toLowerCase() === 'completed').length,
                                    pending: list.filter(t => (t.status || '').toLowerCase() === 'pending').length,
                                    failed: list.filter(t => (t.status || '').toLowerCase() === 'failed').length,
                                    expired: list.filter(t => (t.status || '').toLowerCase() === 'expired').length
                                };
                            })()}
                            currentRole={currentRole}
                            user={user}
                        />

                        {!selectedOrganization ? (
                            <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm">
                                <div className="text-center text-slate-500">
                                    <SwapHoriz className="mx-auto mb-4 text-4xl text-indigo-200" />
                                    <p className="font-medium">Sélectionnez une organisation pour afficher les transactions</p>
                                </div>
                            </div>
                        ) : loading && (transactionsData[activeTab] || []).length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm">
                                <div className="text-center">
                                    <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-slate-600">Chargement des transactions...</p>
                                </div>
                            </div>
                        ) : (
                            <TransactionTable
                                transactions={transactionsData[activeTab] || []}
                                onTransactionSelect={setSelectedTransaction}
                                type={activeTab}
                                currentPage={currentPage}
                                nextPage={nextPage}
                                prevPage={prevPage}
                                onPageChange={setCurrentPage}
                            />
                        )}

                        {selectedTransaction && (
                            <TransactionModal
                                transaction={selectedTransaction}
                                onClose={() => setSelectedTransaction(null)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CashierTransactionsPage;
